import React, { useState } from "react";
import { Quote, ArrowRight, ExternalLink } from "lucide-react";
import { caseStudiesData } from "../data/mock";

export const CaseStudiesSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const active = caseStudiesData[activeIndex];

  return (
    <section
      id="case-studies"
      className="py-24 lg:py-32 bg-[#0A0A0A] relative overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-[#F76B1C]/[0.015] rounded-full blur-[150px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-14 lg:mb-16">
          <span className="text-[#F76B1C] text-sm font-semibold uppercase tracking-[0.15em]">
            Case Studies
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
            Trusted by the World&apos;s Best Teams
          </h2>
          <p className="mt-6 text-lg text-white/45 leading-relaxed">
            From NASA to Ford, see how leading organisations use Formlabs SLA
            printers to accelerate innovation and production.
          </p>
        </div>

        {/* Featured case study */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 mb-10">
          {/* Image */}
          <div className="relative rounded-2xl overflow-hidden min-h-[300px] lg:min-h-[420px] bg-[#111111]">
            <img loading="lazy"
              src={active.image}
              alt={active.company}
              className="absolute inset-0 w-full h-full object-cover"
              key={active.company}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            {/* Badges */}
            <div className="absolute top-5 left-5 flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-[#F76B1C]/90 text-white text-xs font-bold">
                {active.industry}
              </span>
              <span className="px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-white/80 text-xs font-medium border border-white/10">
                {active.application}
              </span>
            </div>
          </div>

          {/* Quote + content */}
          <div className="flex flex-col justify-center p-2 lg:p-4">
            <div className="w-10 h-10 rounded-xl bg-[#F76B1C]/10 flex items-center justify-center mb-6">
              <Quote size={20} className="text-[#F76B1C]" />
            </div>

            <blockquote className="text-lg lg:text-xl text-white/80 leading-relaxed mb-8 font-light italic">
              &ldquo;{active.quote}&rdquo;
            </blockquote>

            <div className="mb-8">
              <p className="text-white font-semibold text-base">
                {active.person}
              </p>
              <p className="text-white/40 text-sm mt-0.5">{active.title}</p>
            </div>

            <a
              href={active.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#F76B1C] hover:text-[#FF8C42] font-semibold text-sm transition-colors duration-200 group w-fit"
            >
              Read the Full Story
              <ExternalLink
                size={14}
                className="group-hover:translate-x-0.5 transition-transform"
              />
            </a>
          </div>
        </div>

        {/* Case study selector */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {caseStudiesData.map((cs, i) => (
            <button
              key={cs.company}
              onClick={() => setActiveIndex(i)}
              className={`group relative p-4 rounded-xl text-left transition-all duration-300 ${
                activeIndex === i
                  ? "bg-[#111111] border border-[#F76B1C]/30"
                  : "bg-white/[0.02] border border-white/[0.05] hover:border-white/[0.1] hover:bg-white/[0.04]"
              }`}
            >
              {activeIndex === i && (
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-[#F76B1C] rounded-full" />
              )}
              <p
                className={`text-sm font-bold mb-0.5 ${
                  activeIndex === i ? "text-white" : "text-white/50"
                }`}
              >
                {cs.company}
              </p>
              <p className="text-[11px] text-white/30">{cs.industry}</p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
